import { QueryKey, useQuery } from '@tanstack/react-query'
import { useConnection } from 'hooks/useConnection'
import { useMemo } from 'react'
import { APIFarm, APIFarmsResponse, APIVault, KodiakFarm, StakingTokenType } from 'types/farms'
import { Hex } from 'viem'

import type { BackendQueryOptions } from './api'
import { fetchBackendJson } from './api'

type FarmQueryOptions<TData, TQueryFnData = TData, TError = Error> = BackendQueryOptions<TData, TQueryFnData, TError>

export type FarmsQueryParams = {
  provider?: string
  limit?: number
  offset?: number
  orderBy?: string
  orderDirection?: 'asc' | 'desc'
  blacklist?: string[]
  minimumFarmTvl?: number
  activeOnly?: boolean
  type?: string[]
  search?: string
}

type NormalizedFarmsParams = {
  provider: NonNullable<FarmsQueryParams['provider']>
  limit: NonNullable<FarmsQueryParams['limit']>
  offset: NonNullable<FarmsQueryParams['offset']>
  orderBy: NonNullable<FarmsQueryParams['orderBy']>
  orderDirection: NonNullable<FarmsQueryParams['orderDirection']>
  blacklist: string[]
  minimumFarmTvl?: number
  activeOnly?: boolean
  type: string[]
  search: string
}

export type KodiakFarmsListResponse = {
  data: KodiakFarm[]
  count: number
  currentPage: number
  totalPages: number
}

const farmKeyBase = (chainId: number) => ['farms', chainId] as const

export const getStakingTokenType = (vault: APIVault | null): StakingTokenType => {
  if (vault === null) {
    return StakingTokenType.GENERIC
  }

  switch (vault.provider) {
    case 'kodiak':
      return StakingTokenType.ISLAND
    case 'v2':
      return StakingTokenType.V2
    case 'charm':
      return StakingTokenType.CHARM
    default:
      return StakingTokenType.GENERIC
  }
}

export const transformAPIFarm = (apiFarm: APIFarm): KodiakFarm => {
  const rewardTokensWithRates = apiFarm.rewardTokens.map((token, index) => ({
    id: token.id as Hex,
    address: token.id as Hex,
    rewardRate: apiFarm.rewardRates[index] || '0',
    symbol: token.symbol,
    name: token.name,
    decimals: token.decimals,
  }))

  return {
    ...apiFarm,
    address: apiFarm.id,
    stakingTokenType: getStakingTokenType(apiFarm.vault),
    rewardTokensWithRates,
  }
}

export const farmQueryKeys = {
  all: (chainId: number) => farmKeyBase(chainId),
  list: (chainId: number, params: NormalizedFarmsParams) =>
    [
      ...farmKeyBase(chainId),
      'list',
      params.provider,
      params.limit,
      params.offset,
      params.orderBy,
      params.orderDirection,
      params.minimumFarmTvl ?? null,
      params.activeOnly ?? null,
      params.search,
      params.blacklist.slice().sort(),
      params.type.slice().sort(),
    ] as const,
  farm: (chainId: number, id: string) => [...farmKeyBase(chainId), 'farm', id] as const,
  userFarms: (chainId: number, address: string) => [...farmKeyBase(chainId), 'user', address] as const,
}

const normalizeFarmsParams = (params: FarmsQueryParams): NormalizedFarmsParams => {
  const {
    provider = 'kodiak',
    limit = 20,
    offset = 0,
    orderBy = 'tvl',
    orderDirection = 'desc',
    blacklist = [],
    minimumFarmTvl,
    activeOnly,
    type = [],
    search,
  } = params

  const trimmedSearch = search?.trim()
  const normalizedSearch = trimmedSearch && trimmedSearch.length > 0 && trimmedSearch.length <= 42 ? trimmedSearch : ''

  return {
    provider,
    limit,
    offset,
    orderBy,
    orderDirection,
    blacklist: [...blacklist],
    minimumFarmTvl,
    activeOnly,
    type: [...type],
    search: normalizedSearch,
  }
}

const buildFarmsSearchParams = (params: NormalizedFarmsParams) => {
  const searchParams = new URLSearchParams({
    provider: params.provider,
    limit: params.limit.toString(),
    offset: params.offset.toString(),
    orderBy: params.orderBy,
    orderDirection: params.orderDirection,
  })

  params.blacklist.forEach((id) => searchParams.append('blacklist', id))

  if (params.minimumFarmTvl !== undefined) {
    searchParams.append('minimumFarmTvl', params.minimumFarmTvl.toString())
  }

  if (params.activeOnly !== undefined) {
    searchParams.append('activeOnly', params.activeOnly.toString())
  }

  if (params.type.length > 0) {
    params.type.forEach((t) => searchParams.append('type', t))
  }

  if (params.search) {
    searchParams.append('search', params.search)
  }

  return searchParams.toString()
}

const fetchFarmById = async (id: string, chainId: number, signal?: AbortSignal): Promise<KodiakFarm> => {
  const data = await fetchBackendJson<APIFarm>(`/farms/${id}?chainId=${chainId}`, {
    method: 'GET',
    signal,
  })
  return transformAPIFarm(data)
}

const fetchFarms = async (
  params: NormalizedFarmsParams,
  chainId: number,
  signal?: AbortSignal
): Promise<KodiakFarmsListResponse> => {
  const searchParams = buildFarmsSearchParams(params)
  const data = await fetchBackendJson<APIFarmsResponse>(`/farms?${searchParams}&chainId=${chainId}`, {
    method: 'GET',
    signal,
  })
  const farms = data.data.map(transformAPIFarm)
  const currentPage = Math.floor(params.offset / params.limit) + 1
  const totalPages = Math.ceil(data.count / params.limit)

  return {
    data: farms,
    count: data.count,
    currentPage,
    totalPages,
  }
}

const fetchUserFarms = async (address: string, signal?: AbortSignal): Promise<KodiakFarm[]> => {
  const data = await fetchBackendJson<APIFarmsResponse>(`/farms/user/${address}`, {
    method: 'GET',
    signal,
  })
  return data.data.map(transformAPIFarm)
}

export const useFarmById = (id: Hex | string | null | undefined, options?: FarmQueryOptions<KodiakFarm>) => {
  const { chainId } = useConnection()
  const normalizedId = (id ?? '').toLowerCase()
  const {
    skip,
    enabled: optionEnabled,
    queryKey,
    retry = false,
    refetchOnWindowFocus = false,
    ...queryOptions
  } = options ?? {}
  const enabled = (optionEnabled ?? true) && !skip && Boolean(normalizedId)

  return useQuery<KodiakFarm, Error>({
    queryKey: (queryKey ?? farmQueryKeys.farm(chainId, normalizedId)) as QueryKey,
    queryFn: ({ signal }) => fetchFarmById(normalizedId, chainId, signal),
    enabled,
    retry,
    refetchOnWindowFocus,
    ...queryOptions,
  })
}

export const useFarms = (
  params: FarmsQueryParams = {},
  options?: FarmQueryOptions<KodiakFarmsListResponse, KodiakFarmsListResponse>
) => {
  const { chainId } = useConnection()
  const normalizedParams = useMemo(
    () => normalizeFarmsParams(params),
    [
      params.provider,
      params.limit,
      params.offset,
      params.orderBy,
      params.orderDirection,
      params.minimumFarmTvl,
      params.activeOnly,
      params.search,
      params.blacklist?.join(','),
      params.type?.join(','),
    ]
  )
  const {
    skip,
    enabled: optionEnabled,
    queryKey,
    retry = false,
    refetchOnWindowFocus = false,
    ...queryOptions
  } = options ?? {}
  const enabled = (optionEnabled ?? true) && !skip

  return useQuery<KodiakFarmsListResponse, Error>({
    queryKey: (queryKey ?? farmQueryKeys.list(chainId, normalizedParams)) as QueryKey,
    queryFn: ({ signal }) => fetchFarms(normalizedParams, chainId, signal),
    enabled,
    retry,
    refetchOnWindowFocus,
    ...queryOptions,
  })
}

export const useUserFarms = (address: Hex | string | null | undefined, options?: FarmQueryOptions<KodiakFarm[]>) => {
  const { chainId } = useConnection()
  const normalizedAddress = (address ?? '').toLowerCase()
  const {
    skip,
    enabled: optionEnabled,
    queryKey,
    retry = false,
    refetchOnWindowFocus = false,
    ...queryOptions
  } = options ?? {}
  const enabled = (optionEnabled ?? true) && !skip && Boolean(normalizedAddress)

  return useQuery<KodiakFarm[], Error>({
    queryKey: (queryKey ?? farmQueryKeys.userFarms(chainId, normalizedAddress)) as QueryKey,
    queryFn: ({ signal }) => fetchUserFarms(normalizedAddress, signal),
    enabled,
    retry,
    refetchOnWindowFocus,
    ...queryOptions,
  })
}
